import { useState } from 'react';
import { PLAN_TERMS } from '@wallflower/shared';
import type { OpsOverview, OpsSessionSummary } from '@wallflower/shared';
import { formatBytes, formatRelative, formatUntil } from '../lib/format.ts';
import OpsSessionDetail from './OpsSessionDetail.tsx';

type Filter = 'all' | 'live' | 'attention' | 'expiring';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'live', label: 'Someone watching' },
  { value: 'attention', label: 'Needs a look' },
  { value: 'expiring', label: 'Expiring soon' },
];

const EXPIRING_SOON_MS = 3 * 24 * 60 * 60 * 1000;

function needsAttention(session: OpsSessionSummary): boolean {
  return session.failed > 0 || session.processing > 0;
}

/** Healthy, working on something, or something broke. */
function statusColor(session: OpsSessionSummary): string {
  if (session.failed > 0) return 'var(--color-danger)';
  if (session.processing > 0) return 'var(--color-sulphur)';
  return 'var(--color-stem)';
}

interface Props {
  overview: OpsOverview;
  /** Re-poll after a mutation, so the numbers stop lying immediately. */
  onChanged: () => void;
}

export default function OpsSessionList({ overview, onChanged }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const { instance, sessions } = overview;

  const shown = sessions.filter((session) => {
    if (filter === 'live') return session.viewers > 0;
    if (filter === 'attention') return needsAttention(session);
    if (filter === 'expiring') return session.expiresAt - Date.now() < EXPIRING_SOON_MS;
    return true;
  });

  return (
    <div className="grid gap-6">
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Events" value={String(instance.sessionCount)} />
        <Stat label="Watching" value={String(instance.viewers)} />
        <Stat label="Processing" value={String(instance.processing)} />
        <Stat
          label="Failed"
          value={String(instance.failed)}
          tone={instance.failed > 0 ? 'danger' : undefined}
        />
        <Stat label="Storage" value={`≈ ${formatBytes(instance.totalApproxBytes)}`} />
        <Stat label="Up" value={formatUptime(instance.uptimeSeconds)} />
      </section>

      <section className="panel p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="eyebrow">Events on this server</h2>
          <p className="text-xs text-muted">
            Sizes are the stored originals only, so they read low. Open an event for the real
            number.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              aria-pressed={filter === option.value}
              className="rounded-full border px-3 py-1.5 text-xs"
              style={{
                borderColor: filter === option.value ? 'var(--color-petal)' : 'var(--color-edge)',
                color: filter === option.value ? 'var(--color-chalk)' : 'var(--color-muted)',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="mt-8 mb-4 text-center text-sm text-muted">
            {sessions.length === 0
              ? 'No events have been created on this server yet.'
              : 'Nothing in this view.'}
          </p>
        ) : (
          <ul className="mt-4 grid gap-2">
            {shown.map((session) => (
              <li key={session.id} className="rounded-lg border border-edge p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: statusColor(session) }}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate font-medium">
                      {session.name}
                      {/* Three plans left, and which one a wall is on is the thing
                          an operator scanning this list most wants to see. */}
                      <span
                        className="rounded-full border border-edge px-2 py-0.5 text-[0.65rem] tracking-wide uppercase"
                        style={{
                          color:
                            session.plan === 'pro'
                              ? 'var(--color-stem)'
                              : session.plan === 'unpaid'
                                ? 'var(--color-sulphur)'
                                : 'var(--color-muted)',
                        }}
                      >
                        {/* The label, not the identifier: the detail pane beside
                            this one already reads from the same table. */}
                        {PLAN_TERMS[session.plan].label}
                      </span>
                    </p>
                    <p className="font-mono text-xs text-muted">{session.id}</p>
                  </div>

                  <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
                    <span>
                      {session.ready}/{session.mediaCount} ready
                      {session.failed > 0 ? (
                        <span style={{ color: 'var(--color-danger)' }}> · {session.failed} failed</span>
                      ) : null}
                      {session.processing > 0 ? (
                        <span style={{ color: 'var(--color-sulphur)' }}>
                          {' '}
                          · {session.processing} working
                        </span>
                      ) : null}
                    </span>
                    <span>{session.messageCount} messages</span>
                    <span>≈ {formatBytes(session.approxBytes)}</span>
                    <span>{session.viewers} watching</span>
                    <span>
                      {session.lastUploadAt
                        ? `last upload ${formatRelative(session.lastUploadAt)}`
                        : 'nothing uploaded'}
                    </span>
                    <span>expires {formatUntil(session.expiresAt)}</span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-ghost shrink-0 px-3 py-1.5 text-xs"
                    aria-expanded={openId === session.id}
                    onClick={() => setOpenId(openId === session.id ? null : session.id)}
                  >
                    {openId === session.id ? 'Close' : 'Troubleshoot'}
                  </button>
                </div>

                {openId === session.id ? (
                  <OpsSessionDetail
                    sessionId={session.id}
                    onChanged={onChanged}
                    onDeleted={() => {
                      setOpenId(null);
                      onChanged();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div className="panel p-3">
      <p className="eyebrow">{label}</p>
      <p
        className="font-display mt-1 text-xl font-semibold"
        style={tone === 'danger' ? { color: 'var(--color-danger)' } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 48 * 3600) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}
